// src/helpers/pdfOrdenCobroRemito.js
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
// import moment from "moment";  // 👈 ya no lo usamos
import { getOrdenCobroRemitoById } from "./rutaOrdenCobroRemitos";
import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

const $ = (n) => fmtNum(n);

// 🔹 Defaults iguales para todas las OCR (San Pancracio)
const DEFAULT_LOGO_URL = "/SanPancracio.png"; // archivo en /public
const DEFAULT_EMPRESA = {
  nombre: "SAN PANCRACIO SRL",
  linea1: "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
  linea2: "Tel: 0381-3008829 • contacto@sanpancracio.com",
};

/**
 * Genera PDF de Orden de Cobro de Remitos
 * con el mismo encabezado que los Remitos (logo + datos empresa).
 *
 * Puede recibir opcionalmente opts = { logoUrl, empresa }
 */
export async function generarPDFOrdenCobroRemito(id, opts = {}) {
  const r = await getOrdenCobroRemitoById(id);
  const oc = r?.orden;
  if (!oc) return alert("No se encontró la orden.");

  const logoUrl = opts.logoUrl || DEFAULT_LOGO_URL;
  const empresa = { ...DEFAULT_EMPRESA, ...(opts.empresa || {}) };

  let logoDataURL = null;
  try {
    if (logoUrl) {
      logoDataURL = await loadImageAsDataURL(logoUrl);
    }
  } catch (e) {
    console.error("Error cargando logo OCR:", e);
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ----- ENCABEZADO -----
  const yTop = 12;

  // 👉 Usamos el header solo con el nombre (sin línea1 / línea2)
  const empresaSoloNombre = { ...empresa, linea1: "", linea2: "" };
  drawHeader(doc, yTop, logoDataURL, empresaSoloNombre);

  // 👉 Ahora dibujamos nosotros linea1 y linea2 un poquito más abajo
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (empresa.linea1) {
    doc.text(empresa.linea1, 105, yTop + 12, { align: "center" });
  }
  if (empresa.linea2) {
    doc.text(empresa.linea2, 105, yTop + 17, { align: "center" });
  }

  // Dejamos la misma separación que el Remito:
  // línea horizontal del header + un renglón en blanco
  let y = yTop + 34;

  // ----- TÍTULO + DATOS GENERALES -----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("ORDEN DE COBRO (REMITOS)", 105, y, { align: "center" });
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const cliNombre = S(
    oc?.cliente?.razonsocial ||
      oc?.cliente?.nombre ||
      oc?.cliente ||
      "-"
  );

  doc.text(`N°: ${oc.numero || "-"}`, 12, y);
  doc.text(`Fecha: ${fmtDate(oc.fecha)}`, 198, y, { align: "right" });
  y += 5;
  doc.text(`Cliente: ${cliNombre}`, 12, y);
  y += 8;

  // ----- TABLA APLICACIONES REMITOS -----
  autoTable(doc, {
    startY: y,
    margin: { left: 12, right: 12 },
    head: [["Remito", "Fecha", "Pactado", "Cobrado Ant.", "Aplicado"]],
    body: (oc.aplicaciones || []).map((a) => {
      const rem = a.remito || {};
      const nro = rem.codrem != null ? String(rem.codrem).padStart(6, "0") : "";
      return [
        `#${nro}`,
        fmtDate(rem.fecha),
        `$ ${$(a.pactado)}`,
        `$ ${$(a.cobradoAnterior ?? a.pagadoAnterior)}`,
        `$ ${$(a.aplicado)}`,
      ];
    }),
    styles: { fontSize: 9, halign: "right" },
    headStyles: { halign: "center", fillColor: [51, 122, 183] },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
    },
  });

  let fy = doc.lastAutoTable.finalY + 8;

  // ----- TABLA COBROS -----
  autoTable(doc, {
    startY: fy,
    margin: { left: 12, right: 12 },
    head: [["Medio", "Origen / Ref.", "Importe"]],
    body: (oc.cobros || oc.pagos || []).map((p) => [
      S(p.medio || ""),
      S(
        p?.caja?.descripcion ||
          p?.cuentaBancaria?.alias ||
          p?.referencia ||
          ""
      ),
      `$ ${$(p.importe)}`,
    ]),
    styles: { fontSize: 9, halign: "right" },
    headStyles: { halign: "center", fillColor: [51, 122, 183] },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "left" },
    },
  });

  fy = doc.lastAutoTable.finalY + 8;

  // ----- TABLA RETENCIONES (SI HAY) -----
  if ((oc.retenciones || []).length) {
    autoTable(doc, {
      startY: fy,
      margin: { left: 12, right: 12 },
      head: [["Tipo", "Comp.", "Importe"]],
      body: oc.retenciones.map((r) => [
        S(r.tipo || ""),
        S(r.comprobante || ""),
        `$ ${$(r.importe)}`,
      ]),
      styles: { fontSize: 9, halign: "right" },
      headStyles: { halign: "center", fillColor: [51, 122, 183] },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
      },
    });

    fy = doc.lastAutoTable.finalY + 8;
  }

  // ----- TOTALES -----
  const totAplicado = Number(oc?.totales?.aplicado || 0);
  const totRets = Number(oc?.totales?.retenciones || 0);
  const neto =
    Number(oc?.totales?.netoCobrado || 0) ||
    (oc.cobros || oc.pagos || []).reduce(
      (a, m) => a + Number(m.importe || 0),
      0
    ) -
      (oc.retenciones || []).reduce(
        (a, m) => a + Number(m.importe || 0),
        0
      );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`£ Aplicado: $ ${$(totAplicado)}`, 12, fy);
  fy += 5;
  doc.text(`£ Deducciones: $ ${$(totRets)}`, 12, fy);
  fy += 5;
  doc.text(`£ Neto Cobrado: $ ${$(neto)}`, 12, fy);

  // ----- PIE -----
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.text(
    "Documento electrónico generado por el sistema San Pancracio — Orden de Cobro de Remitos",
    12,
    292
  );

  doc.save(`${oc.numero || "OCR"}.pdf`);
}
